/**
 * QrLoginPage — public page that scans a QR badge and exchanges it for a JWT.
 * One-stop kiosk-style login for students/teachers/parents/delegates.
 *
 * Kiosk mode: pass `?kiosk=1` to keep the camera scanner permanently open
 * and immediately return to the scanner after each successful login (the
 * exchanged JWT is NOT persisted across the session — the user is redirected
 * to their landing, and the kiosk is meant to be on a guest device).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, QrCode, ScanLine, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useAuthStore } from '@/lib/store';
import afterLogin from '@/lib/afterLogin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QrLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setAuth } = useAuthStore();
  const kiosk = params.get('kiosk') === '1';
  const [manual, setManual] = useState('');
  const [exchanging, setExchanging] = useState(false);
  // In kiosk mode the scanner stays mounted from page load.
  const [scannerOpen, setScannerOpen] = useState(kiosk);
  const [pin, setPin] = useState('');
  const [pinUnlocked, setPinUnlocked] = useState(!kiosk);
  // PIN is configured per-device (localStorage). First-time setup happens
  // when kiosk operator types a 4-digit PIN and confirms — stored hashed.
  const [needsPinSetup, setNeedsPinSetup] = useState(kiosk && !localStorage.getItem('kiosk_pin_hash'));

  const exchange = async (rawToken) => {
    if (!rawToken) return;
    setExchanging(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/qr/exchange`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_token: rawToken }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'QR login failed');
      setAuth(data.access_token, data.user, data.user_type, data.tenant_id || data.event_id, []);
      toast.success(`Welcome, ${data.user?.name || 'there'}!`);
      const redirect = params.get('redirect') || '';
      await afterLogin({ navigate, userType: data.user_type, tenantId: data.tenant_id, redirect });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExchanging(false);
    }
  };

  // Mount scanner when user opens it
  useEffect(() => {
    if (!scannerOpen || !pinUnlocked) return;
    const scanner = new Html5QrcodeScanner('qr-login-reader',
      { fps: 10, qrbox: { width: 250, height: 250 } }, false);
    scanner.render(async (decoded) => {
      scanner.clear().catch(() => {});
      // In non-kiosk we close the scanner; kiosk mode is handled by the navigate redirect.
      if (!kiosk) setScannerOpen(false);
      await exchange(decoded);
    }, () => {/* ignore per-frame errors */ });
    return () => { scanner.clear().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerOpen, pinUnlocked]);

  // ----- KIOSK PIN -----
  const hashPin = async (raw) => {
    const enc = new TextEncoder().encode(raw + '|kiosk-salt-v1');
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  };
  const setupPin = async () => {
    if (pin.length < 4) { toast.error('Use at least 4 digits'); return; }
    const hash = await hashPin(pin);
    localStorage.setItem('kiosk_pin_hash', hash);
    setNeedsPinSetup(false);
    setPin('');
    setPinUnlocked(true);
    toast.success('Kiosk PIN set. Scanner is ready.');
  };
  const tryUnlock = async () => {
    const stored = localStorage.getItem('kiosk_pin_hash');
    const candidate = await hashPin(pin);
    if (candidate === stored) { setPinUnlocked(true); setPin(''); }
    else toast.error('Wrong PIN');
  };

  if (kiosk && needsPinSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
        <Card className="max-w-sm w-full" data-testid="kiosk-pin-setup">
          <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-indigo-500" /> Set kiosk PIN</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">First time on this device. Choose a 4+ digit PIN that staff will use to unlock the kiosk if it's locked.</p>
            <Input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" data-testid="kiosk-pin-setup-input" />
            <Button onClick={setupPin} className="w-full" data-testid="kiosk-pin-setup-btn">Save PIN</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (kiosk && !pinUnlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
        <Card className="max-w-sm w-full" data-testid="kiosk-pin-lock">
          <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-indigo-500" /> Kiosk locked</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter PIN" onKeyDown={(e) => e.key === 'Enter' && tryUnlock()} data-testid="kiosk-pin-input" />
            <Button onClick={tryUnlock} className="w-full" data-testid="kiosk-pin-unlock-btn">Unlock</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <Card className="max-w-md w-full" data-testid="qr-login-page">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><QrCode className="h-5 w-5 text-indigo-500" /> {kiosk ? 'Kiosk mode' : 'Sign in with QR'}</span>
            {kiosk && (
              <Button size="sm" variant="ghost" onClick={() => { setPinUnlocked(false); setScannerOpen(false); }} data-testid="kiosk-lock-btn">
                <Lock className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {scannerOpen ? (
            <div id="qr-login-reader" className="w-full" />
          ) : (
            <>
              <Button onClick={() => setScannerOpen(true)} className="w-full" disabled={exchanging} data-testid="qr-login-open-camera">
                <ScanLine className="h-4 w-4 mr-2" /> Open camera scanner
              </Button>
              <div className="text-center text-xs text-slate-400">— or paste your QR token —</div>
              <div className="flex gap-2">
                <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="qrlogin:..." data-testid="qr-login-manual-input" />
                <Button onClick={() => exchange(manual.trim())} disabled={!manual.trim() || exchanging} data-testid="qr-login-manual-submit">
                  {exchanging ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
                </Button>
              </div>
            </>
          )}
          <p className="text-[11px] text-slate-500 text-center">QR badges are issued by your school/event admin. Lost yours? Ask the front desk to print a new one.</p>
        </CardContent>
      </Card>
    </div>
  );
}
